/**
 * Service editor — shared controls. Verbatim port of the control set from
 * screens-service-editor.jsx (SvcSelect / SvcNumField / SvcTagField / SvcRow /
 * SvcSeg / SvcSectionHead / SvcLocationPicker / Radio / ImageDropField).
 */
import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Btn, Checkbox, Icon, Input, SegmentedControl, T } from "@v0y4ge/jrni-ui";
import { listLocations } from "@v0y4ge/api-client";
import { LOCATIONS_OPTS } from "../../shell/request-opts";
import { useT } from "../../lib/i18n";

export function SvcSelect({ value, onChange, options, disabled, style }: {
  value: string; onChange?: (v: string) => void; options: { id: string; label: string }[]; disabled?: boolean; style?: CSSProperties;
}) {
  return (
    <div style={{ position: "relative", display: "inline-flex", width: "100%" }}>
      <select value={value} disabled={disabled} onChange={(e) => onChange && onChange(e.target.value)}
        style={{ width: "100%", height: 40, padding: "0 34px 0 12px", border: `1px solid ${T.n4}`, borderRadius: 8, background: T.white, color: T.n1, fontSize: 14, fontFamily: T.font, cursor: disabled ? "default" : "pointer", appearance: "none", ...style }}>
        {options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}
      </select>
      <span style={{ position: "absolute", right: 11, top: "50%", transform: "translateY(-50%)", pointerEvents: "none", color: T.soft }}><Icon.chev size={14} /></span>
    </div>
  );
}

export function SvcNumField({ value, onChange, min = 0, max = 100000, step = 1, unit, prefix, width = 110, disabled }: {
  value: number; onChange: (v: number) => void; min?: number; max?: number; step?: number; unit?: string; prefix?: string; width?: number; disabled?: boolean;
}) {
  return (
    <div style={{ display: "inline-flex", alignItems: "center", height: 40, border: `1px solid ${T.n4}`, borderRadius: 8, background: disabled ? T.n6 : T.white, overflow: "hidden", width }}>
      {prefix ? <span style={{ paddingLeft: 11, color: T.soft, fontSize: 14 }}>{prefix}</span> : null}
      <input type="number" value={value} min={min} max={max} step={step} disabled={disabled}
        onChange={(e) => { const v = e.target.value === "" ? 0 : Number(e.target.value); onChange(Math.max(min, Math.min(max, v))); }}
        style={{ width: "100%", height: "100%", border: "none", outline: "none", background: "transparent", padding: prefix ? "0 6px 0 4px" : "0 6px 0 11px", fontSize: 14, fontFamily: T.font, color: T.n1 }} />
      {unit ? <span style={{ paddingRight: 11, color: T.soft, fontSize: 12.5, whiteSpace: "nowrap" }}>{unit}</span> : null}
    </div>
  );
}

/* Lightweight tag input — Enter or comma adds; click × removes. */
export function SvcTagField({ tags, onChange, placeholder = "Add and press Enter", disabled }: {
  tags: string[]; onChange: (v: string[]) => void; placeholder?: string; disabled?: boolean;
}) {
  const [draft, setDraft] = useState("");
  const add = () => { const v = draft.trim(); if (v && !tags.includes(v)) onChange([...tags, v]); setDraft(""); };
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: 6, minHeight: 40, border: `1px solid ${T.n4}`, borderRadius: 8, background: disabled ? T.n6 : T.white, alignItems: "center" }}>
      {tags.map((t) => (
        <span key={t} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 6px 3px 10px", borderRadius: 999, background: T.p4, color: T.p1, fontSize: 12.5, fontWeight: 600 }}>
          {/* ui-raw: inline tag-chip remove glyph — TagInput/Chip owns this (group 6) */}
          {t}{!disabled ? <button onClick={() => onChange(tags.filter((x) => x !== t))} style={{ border: "none", background: "transparent", color: T.p1, cursor: "pointer", display: "inline-flex", padding: 0 }}><Icon.x size={12} /></button> : null}
        </span>
      ))}
      {!disabled ? <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={tags.length ? "" : placeholder}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === ",") { e.preventDefault(); add(); } else if (e.key === "Backspace" && !draft && tags.length) onChange(tags.slice(0, -1)); }}
        onBlur={add} style={{ flex: 1, minWidth: 90, border: "none", outline: "none", background: "transparent", fontSize: 13.5, fontFamily: T.font, color: T.n1, height: 26 }} /> : null}
    </div>
  );
}

/* A labelled control row used throughout the editor. */
export function SvcRow({ label, hint, children, full }: { label: string; hint?: string; children: ReactNode; full?: boolean }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: full ? "1fr" : "210px 1fr", gap: full ? 8 : 18, padding: "16px 0", borderTop: `1px solid ${T.border}`, alignItems: full ? "stretch" : "start" }}>
      <div>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: T.n1 }}>{label}</div>
        {hint ? <div style={{ fontSize: 12, color: T.soft, marginTop: 3, lineHeight: 1.4 }}>{hint}</div> : null}
      </div>
      <div style={{ minWidth: 0 }}>{children}</div>
    </div>
  );
}

/* Segmented control. */
export function SvcSeg({ value, onChange, options, disabled, ariaLabel }: { value: string; onChange: (v: string) => void; options: { id: string; label: string }[]; disabled?: boolean; ariaLabel?: string }) {
  /* The DS `SegmentedControl` under this file's historic `{ id, label }`
     signature (adopted 2026-08-11). jrni-ui already ships this control with the
     same contract — options / value / onChange — PLUS what the hand-roll never
     had: `role="radiogroup"` + `role="radio"`/`aria-checked` semantics, a size
     scale, and per-option `disabled` (the group-level flag maps straight onto
     it). The old body painted the selected segment a `T.p1` fill with a raw
     `T.white` ink — BOTH halves invert in dark, so the selected segment went
     dark-on-dark; the DS active chip is a `T.p3` tint with `T.p1` ink and reads
     in both themes. `ariaLabel` is optional and unset by default, so existing
     call sites are untouched. */
  return (
    <SegmentedControl
      value={value}
      onChange={onChange}
      ariaLabel={ariaLabel}
      options={options.map((o) => ({ value: o.id, label: o.label, disabled }))}
    />
  );
}

export function SvcSectionHead({ icon, title, desc }: { icon: ReactNode; title: string; desc: string }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 13, marginBottom: 4 }}>
      <span style={{ width: 38, height: 38, borderRadius: 10, background: T.p3, color: T.p1, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{icon}</span>
      <div><div style={{ fontSize: 18, fontWeight: 700, color: T.n1, letterSpacing: -0.3 }}>{title}</div><div style={{ fontSize: 13, color: T.soft }}>{desc}</div></div>
    </div>
  );
}

export function Radio({ on }: { on: boolean }) {
  return <span style={{ width: 18, height: 18, borderRadius: 999, border: `1.5px solid ${on ? T.p1 : T.n4}`, background: on ? T.p1 : T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>{on ? <span style={{ width: 7, height: 7, borderRadius: 999, background: "#fff" }} /> : null}</span>;
}

/* Drag & drop / browse / URL image field with preview.
 *
 * URL ownership (2026-09-12, memory pass): this field used to call
 * URL.createObjectURL for every accepted file and never revoke anything, with
 * no size limit — so a session of replacing a service image held every
 * intermediate full-resolution file in the tab for as long as the page lived.
 * Now the field owns exactly one object URL at a time and revokes it at every
 * lifetime boundary: replaced, removed, parent-overwritten, unmounted. Files
 * are also downscaled before they are held (see prepareImage).
 */
const MAX_EDGE = 1600;              // px on the long edge — well past any preview's need
const MAX_BYTES = 1_500_000;        // above this, re-encode rather than hold the original
const REFUSE_BYTES = 25_000_000;    // refuse outright, with a stated reason

/** The blob actually worth holding for `file`: the file itself when it is
 *  already small enough, otherwise a downscaled re-encode. Falls back to the
 *  original on any failure — a preview that shows the real file is better than
 *  an error, and the cap above already bounds the worst case. */
async function prepareImage(file: File): Promise<Blob> {
  try {
    if (file.size <= MAX_BYTES && typeof createImageBitmap !== "function") return file;
    const bmp = await createImageBitmap(file);
    try {
      const scale = Math.min(1, MAX_EDGE / Math.max(bmp.width, bmp.height));
      if (scale === 1 && file.size <= MAX_BYTES) return file;
      const w = Math.max(1, Math.round(bmp.width * scale));
      const h = Math.max(1, Math.round(bmp.height * scale));
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) return file;
      ctx.drawImage(bmp, 0, 0, w, h);
      const blob = await new Promise<Blob | null>((res) => canvas.toBlob(res, "image/webp", 0.9));
      return blob && blob.size < file.size ? blob : file;
    } finally { bmp.close(); }
  } catch { return file; }
}

export function ImageDropField({ value, onChange, disabled, placeholder = "Drag & drop an image, or click to browse", aspect }: {
  value?: string; onChange: (v: string) => void; disabled?: boolean; placeholder?: string; aspect?: number; variant?: string;
}) {
  const t = useT();
  const fileRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  /** The one object URL this field owns, or null. */
  const owned = useRef<string | null>(null);
  /** Guards against an out-of-order prepareImage resolving after a newer pick. */
  const gen = useRef(0);
  const release = () => {
    if (owned.current) { URL.revokeObjectURL(owned.current); owned.current = null; }
  };
  // The parent replaced or cleared the value (Remove, a pasted URL, a reset
  // from the record): our URL is unreachable now, so let it go.
  useEffect(() => { if (owned.current && value !== owned.current) release(); }, [value]);
  // Unmount: the last URL has no owner left.
  useEffect(() => () => { gen.current++; release(); }, []);
  const load = async (file: File | undefined | null) => {
    if (!file || !file.type.startsWith("image/")) return;
    if (file.size > REFUSE_BYTES) {
      setErr(t("services.editorControls.imageTooLarge", "That image is too large to preview — please use one under 25 MB."));
      return;
    }
    setErr(null);
    const mine = ++gen.current;
    const blob = await prepareImage(file);
    if (mine !== gen.current) return; // superseded, or unmounted
    release();
    const url = URL.createObjectURL(blob);
    owned.current = url;
    onChange(url);
  };
  const clear = () => { release(); setErr(null); onChange(""); };
  return (
    <div>
      <div
        onClick={() => !disabled && fileRef.current?.click()}
        onDragOver={(e) => { if (!disabled) { e.preventDefault(); setOver(true); } }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { if (!disabled) { e.preventDefault(); setOver(false); void load(e.dataTransfer.files?.[0]); } }}
        style={{ borderRadius: 12, border: `1.5px dashed ${over ? T.p1 : T.n4}`, background: over ? T.p4 : T.canvas, cursor: disabled ? "default" : "pointer", overflow: "hidden", position: "relative", aspectRatio: aspect ? String(aspect) : undefined, minHeight: aspect ? undefined : 120, display: "flex", alignItems: "center", justifyContent: "center" }}>
        {value
          ? <img src={value} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
          : <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 8, color: T.soft, padding: 20, textAlign: "center" }}><Icon.upload size={20} /><span style={{ fontSize: 13 }}>{placeholder}</span></div>}
        <input ref={fileRef} type="file" accept="image/*" onChange={(e) => { void load(e.target.files?.[0]); e.target.value = ""; }} style={{ display: "none" }} />
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
        <Input full size="sm" placeholder={t("services.editorControls.orPasteAnImageUrl", "…or paste an image URL")} value={value && !value.startsWith("blob:") ? value : ""} onChange={(e) => { release(); onChange(e.target.value); }} disabled={disabled} />
        {value ? <Btn size="sm" variant="link" disabled={disabled} onClick={clear}>{t("common.actions.remove", "Remove")}</Btn> : null}
      </div>
      {err ? <div style={{ marginTop: 6, fontSize: 12.5, color: T.red1 }}>{err}</div> : null}
    </div>
  );
}

/* Searchable location multi-select, sourced from Locations (typed client).
 * Empty selection = all locations. */
export function SvcLocationPicker({ value, onChange, disabled }: { value: string[]; onChange: (v: string[]) => void; disabled?: boolean }) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);
  const [locs, setLocs] = useState<{ id: string; name: string; address?: string }[]>([]);
  useEffect(() => {
    listLocations({ limit: 200 }, LOCATIONS_OPTS).then((r: any) => {
      const arr = r.locations ?? r.data ?? [];
      setLocs(arr.map((l: any) => ({ id: l.id, name: l.name ?? l.locationName ?? "Location", address: l.address ?? l.location_address ?? undefined })));
    }).catch(() => setLocs([]));
  }, []);
  useEffect(() => {
    if (!open) return;
    const h = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", h); return () => document.removeEventListener("mousedown", h);
  }, [open]);
  const sel = new Set(value || []);
  const filtered = locs.filter((l) => !query.trim() || `${l.name} ${l.address || ""}`.toLowerCase().includes(query.toLowerCase()));
  const toggle = (id: string) => { const next = sel.has(id) ? (value || []).filter((x) => x !== id) : [...(value || []), id]; onChange(next); };
  const nameOf = (id: string) => (locs.find((l) => l.id === id) || { name: id }).name;
  const summary = !value || value.length === 0 ? "All locations" : value.length === locs.length ? "All locations" : `${value.length} location${value.length === 1 ? "" : "s"}`;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      {/* ui-raw: MultiSelect trigger (summary + caret) — MultiSelect's pattern (group 3) */}
      <button type="button" disabled={disabled} onClick={() => setOpen((o) => !o)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 8, minHeight: 40, padding: "7px 12px", border: `1px solid ${open ? T.p1 : T.n4}`, borderRadius: 8, background: disabled ? T.n6 : T.white, cursor: disabled ? "default" : "pointer", fontFamily: T.font, textAlign: "left" }}>
        <Icon.loc size={14} style={{ color: T.soft, flexShrink: 0 }} />
        <span style={{ flex: 1, display: "flex", flexWrap: "wrap", gap: 6, minWidth: 0 }}>
          {(!value || value.length === 0 || value.length === locs.length)
            ? <span style={{ fontSize: 14, color: (!value || value.length === 0) ? T.soft : T.n1 }}>{t("services.editorControls.allLocations", "All locations")}</span>
            : value.slice(0, 4).map((id) => <span key={id} style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 6px 2px 9px", borderRadius: 999, background: T.p4, color: T.p1, fontSize: 12, fontWeight: 600 }}>{nameOf(id)}{!disabled ? <span onClick={(e) => { e.stopPropagation(); toggle(id); }} style={{ display: "inline-flex", cursor: "pointer" }}><Icon.x size={11} /></span> : null}</span>)}
          {value && value.length > 4 && value.length !== locs.length ? <span style={{ fontSize: 12, color: T.soft, alignSelf: "center" }}>+{value.length - 4}</span> : null}
        </span>
        <span style={{ fontSize: 12, color: T.soft, flexShrink: 0 }}>{summary}</span>
        <Icon.chev size={14} style={{ color: T.soft, flexShrink: 0 }} />
      </button>
      {open ? (
        <div style={{ position: "absolute", top: "calc(100% + 6px)", left: 0, right: 0, zIndex: 60, background: T.white, border: `1px solid ${T.border}`, borderRadius: 11, boxShadow: T.shadowLg, overflow: "hidden" }}>
          <div style={{ padding: 10, borderBottom: `1px solid ${T.border}` }}>
            <Input full leading={<Icon.search size={14} />} size="sm" autoFocus placeholder={t("services.editorControls.searchLocations", "Search locations…")} value={query} onChange={(e) => setQuery(e.target.value)} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", padding: "8px 12px", borderBottom: `1px solid ${T.border}`, background: T.canvas }}>
            <Btn size="sm" variant="link" onClick={() => onChange(locs.map((l) => l.id))}>{t("services.editorControls.selectAll", "Select all")}</Btn>
            <Btn size="sm" variant="link" onClick={() => onChange([])}>{t("services.editorControls.clearAllLocations", "Clear (all locations)")}</Btn>
          </div>
          <div style={{ maxHeight: 260, overflow: "auto", padding: 6 }}>
            {filtered.length === 0 ? <div style={{ padding: 18, textAlign: "center", color: T.soft, fontSize: 13 }}>{t("services.editorControls.noLocationsMatch", "No locations match.")}</div> : filtered.map((l) => {
              const on = sel.has(l.id);
              return (
                /* ui-raw: MultiSelect option row (checkbox + label) — MultiSelect's pattern (group 3) */
                <button key={l.id} type="button" onClick={() => toggle(l.id)} style={{ width: "100%", display: "flex", alignItems: "center", gap: 10, padding: "9px 10px", borderRadius: 8, border: "none", background: on ? T.p4 : "transparent", cursor: "pointer", fontFamily: T.font, textAlign: "left" }}>
                  <Checkbox checked={on} />
                  <span style={{ flex: 1, minWidth: 0 }}><span style={{ fontSize: 13.5, fontWeight: 600, color: T.n1, display: "block" }}>{l.name}</span>{l.address ? <span style={{ fontSize: 11.5, color: T.soft }}>{l.address}</span> : null}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}

/* ── Availability-engine adapter ─────────────────────────────────────────
 * The canonical engine (src/availability.jsx: serviceReadiness /
 * serviceCoverageMatrix / effectiveConcurrency / …) ports as its own module
 * in the next Services pass — it is the single source of truth shared with
 * the Coverage section and the publish guard. Until it lands, readiness
 * reports no findings (publish is not blocked) and the matrix is empty; the
 * Coverage section renders its honest empty states. */
export interface ReadinessWarn { level: "high" | "med" | "low"; code: string; msg: string }
export interface CoverageMatrix { rows: { locName: string; channel: string; staff: number | null; resources: number | null; hasHours: boolean }[]; needsStaff: boolean; needsRes: boolean }
export function serviceReadiness(_form: Record<string, any>): ReadinessWarn[] { return []; }
export function serviceCoverageMatrix(_form: Record<string, any>): CoverageMatrix { return { rows: [], needsStaff: false, needsRes: false }; }
